import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@shared/components/ui/Button';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, isAdmin, loading, authInitialized, refreshAdminStatus } = useAuth();
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    console.log('🛡️ AdminRoute: Status check', {
      user: !!user,
      isAdmin,
      loading,
      authInitialized
    });

    const timeout = setTimeout(() => {
      if (loading || !authInitialized) {
        console.warn('⏰ AdminRoute: Timeout reached while checking auth');
        setTimeoutReached(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [loading, authInitialized, user, isAdmin]);

  const handleRefreshAdminStatus = async () => {
    setIsRefreshing(true);
    try {
      console.log('🔄 AdminRoute: Manually refreshing admin status');
      const result = await refreshAdminStatus();
      console.log('🔄 AdminRoute: Refresh result:', result);

      if (!result) {
        console.error('❌ AdminRoute: Admin status refresh failed');
      }
    } catch (error) {
      console.error('❌ AdminRoute: Error refreshing admin status:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  if ((loading || !authInitialized) && !timeoutReached) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  if (timeoutReached && (loading || !authInitialized)) {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Authentication Timeout</h2>
          <p className="text-gray-600 mb-6">
            The admin authentication check is taking longer than expected.
          </p>
          <div className="space-x-3">
            <Button onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
            <Link to="/dashboard">
              <Button variant="outline">Go to Dashboard</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('🔒 AdminRoute: No user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    console.log('🚫 AdminRoute: User is not admin, redirecting to dashboard');
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have admin privileges to access this page.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            If you believe this is an error, try refreshing your admin status.
          </p>
          <div className="space-x-3">
            <Button
              onClick={handleRefreshAdminStatus}
              disabled={isRefreshing}
              className="inline-flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh Admin Status'}
            </Button>
            <Link to="/dashboard">
              <Button variant="outline">Go to Dashboard</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminRoute;
